/**
 * ShadowSystem — shadows between the scan (gaussian splats), models and lights.
 *
 * What it does each frame (`update`, after scripts, before the render):
 * - resolves the scene's shadow settings (shadowSettings.ts) for this device's tier;
 * - configures every authored light: which ones cast, map size, bias, softness (PCF3 / PCF5),
 *   strength (the light's shadowIntensity), one cascade;
 * - lets the splats cast (gsplat.castShadows on every splat, so the engine keeps one shared
 *   splat manager) when the scene asks for it and the tier allows it. WebGL2 draws the splats
 *   into the map with the quad renderer, WebGPU with GSplatShadowRenderer (directional, and
 *   spot / point through gsplatLocalCasters.ts); spot / point maps get the splats only for the
 *   first tier.localSplatCasters such lights (see patchSplatCasterVisibility);
 * - sets models (custom meshes) to cast / receive per their own flags. Every map (directional,
 *   spot, point) is culled from the models and the splat draws only (shadowMapCasters), never
 *   from other draws in the scene's layers (the editor's helpers);
 * - fits each casting directional light's map to the casters (shadowFit.ts) instead of the
 *   camera, through a wrapper around the engine's directional shadow cull, and re-renders a
 *   map only when its light, its casters or its settings changed (SHADOWUPDATE_THISFRAME), so a
 *   static scene renders its maps once, also while the camera moves;
 * - binds up to two directional maps (with their matrices) to the splat shader, which darkens
 *   shadowed splats and removes the shadowed light from relit splats (splatShadowShader.ts);
 * - spot / point lights render into the engine's shadow atlas; the splats take shadows from the
 *   first tier.localSlots of them through a copy of it (localShadows.ts);
 * - keeps an optional ground plane that catches shadows where the scan has no surface.
 *
 * The shader samples the map the engine rendered most recently, with the matrix the engine
 * computed for it (LightRenderData.shadowMatrix). On WebGPU the splat colour is computed in a
 * compute pass that runs before this frame's shadow pass, so that pairing is exact; on WebGL2
 * the vertex shader runs after it, so for one frame after a change the matrix lags the map —
 * the system asks for an extra frame after every change so the picture always settles right.
 */
import * as pc from 'playcanvas';
import {
    MAX_SPLAT_LOCAL_SHADOW_LIGHTS,
    MAX_SPLAT_SHADOW_LIGHTS,
    resolveSceneShadows,
    resolveShadowTier,
    softnessToKernelTexels,
    type LightShadowFields,
    type ResolvedSceneShadows,
    type SceneShadowFields,
    type ShadowTier,
    type ShadowTierInput,
} from './shadowSettings';
import { fitDirectionalShadow, fitShadowMatrix, shadowFitKey, type ShadowFit } from './shadowFit';
import { HASH_SEED, hashNumber, meshCasterHash, shadowStrengthForLight } from './shadowChange';
import { SPLAT_SHADOW_UNIFORMS } from './splatShadowShader';
import { isLightFaceCamera, patchGsplatShadowRenderers } from './gsplatLocalCasters';
import { LocalShadowReceivers, configureLocalAtlas, installOmniCasterChunk, localAtlasEdgePixels, type LocalReceiverSlot } from './localShadows';
import { getShadowMapPlaceholder } from './shadowPlaceholder';
import { collectUnifiedSplatDraws, isSplatMeshInstance } from '../../dynamic-viewer/splatMeshInstances';
import { quantileBox } from '../../utils/renderBounds';
import { createShadowGroundPlane, type ShadowGroundPlane } from './shadowGroundPlane';

export interface ShadowLightEntry {
    entity: pc.Entity;
    config: LightShadowFields;
}

export interface ShadowMeshCaster {
    root: pc.Entity;
    castShadows?: boolean;
    receiveShadows?: boolean;
}

export interface ShadowSystemHost {
    app: pc.AppBase;
    camera: () => pc.CameraComponent | null;
    sceneConfig: () => SceneShadowFields | undefined;
    lights: () => ReadonlyArray<ShadowLightEntry>;
    /** Every gsplat entity in the scene's World layer (main splat, swaps, splat models). */
    splatEntities: () => ReadonlyArray<pc.Entity>;
    meshCasters: () => ReadonlyArray<ShadowMeshCaster>;
    /** Materials carrying the relighting / shadow-receiving shader. */
    receiverMaterials: () => ReadonlyArray<pc.Material>;
    /** Splat relighting: on (shadows then only remove light) and its current fade (viewer toggle). */
    relighting: () => { enabled: boolean; fade: number };
    tierInput: () => ShadowTierInput;
    requestFrame: (reason: string) => void;
    worldLayer: pc.Layer;
    /** Layer drawn after the splats (TranslucentEntityLayer), for the ground plane. */
    afterSplatsLayer: pc.Layer;
    /** Parent for the ground plane. */
    sceneRoot: pc.Entity;
    /** Changes when the streamed splat set changes (LOD); optional. */
    splatVersion?: () => number;
    /** Changes on every frame splats move in place (scene wind); optional. */
    splatMotion?: () => number;
}

interface DirectionalState {
    /** Identity of everything that decides the map's content; a change re-renders it. */
    key: string;
    fit: ShadowFit | null;
    /** The models that cast into the map, as of update (the splats are read at cull time, see shadowMapCasters). */
    meshCasters: pc.MeshInstance[];
    /** The splats cast into the map. */
    splatsCast: boolean;
    /** Explicit caster list handed to the engine's caster cull, filled when the engine culls the map. */
    cullCasters: pc.MeshInstance[];
    pendingRender: boolean;
    renderedFit: ShadowFit | null;
    rendered: boolean;
    /** Streamed splat set the map was last rendered with, and when. */
    splatVersion: number;
    splatVersionAt: number;
    /** Splat motion (wind) the map was last rendered with, and when. */
    splatMotion: number;
    splatMotionAt: number;
    /** WebGPU: the splats' copy of the last rendered map (see copyForReceivers), and the fit it shows. */
    copy: { texture: pc.Texture; target: pc.RenderTarget; fit: ShadowFit } | null;
    /** WebGPU: the fit of a map rendered last frame that still has to be copied. */
    copyFit: ShadowFit | null;
}

interface LocalState {
    key: string;
    /** Streamed splat set the map was last rendered with, and when (the splats cast into it). */
    splatVersion: number;
    splatVersionAt: number;
    /** Splat motion (wind) the map was last rendered with, and when. */
    splatMotion: number;
    splatMotionAt: number;
}

// PlayCanvas 2.23 added `cascadeMask` (the cascades this frame's shadow passes render); the
// wrapper below forwards every argument, so later additions reach the engine too.
type DirectionalShadowCull = (light: pc.Light, comp: pc.LayerComposition, camera: pc.Camera, casters?: pc.MeshInstance[] | null, cascadeMask?: number) => void;
type LocalShadowCull = (light: pc.Light, comp: pc.LayerComposition, casters?: pc.MeshInstance[] | null) => void;
interface LocalShadowRendererInternals {
    cull: LocalShadowCull;
}
interface DirectionalShadowRendererInternals {
    cull: DirectionalShadowCull;
    prepareShadowMap?: (light: pc.Light) => void;
    shadowRenderer: {
        cullShadowCasters(comp: pc.LayerComposition, light: pc.Light, visible: pc.MeshInstance[], camera: pc.Camera, casters: pc.MeshInstance[] | null): void;
    };
}
interface LightInternals {
    _shadowMap?: { texture: pc.Texture; renderTargets: pc.RenderTarget[]; cached: boolean; destroy(): void } | null;
    _shadowCascadeDistances: Float32Array;
    cascades: pc.Vec4[];
    getRenderData(camera: pc.Camera | null, face: number): {
        shadowCamera: pc.Camera & { _node: pc.GraphNode; updateFrustum(): void };
        shadowMatrix: pc.Mat4;
        shadowViewport: pc.Vec4;
        shadowScissor: pc.Vec4;
        visibleCasters: pc.MeshInstance[];
        projectionCompensation: number;
    };
}

const SHADOW_CAMERA_NAME = 'pcShadowCamera';
/** Light-space bias per unit of splat size: large when splats cast (their neighbours on the same surface are in the map). */
const SPLAT_SIZE_BIAS_CASTING = 2.5;
const SPLAT_SIZE_BIAS_MESHES_ONLY = 0.5;
/** Slope-scaled receiver bias, in shadow-map texels (times the surface's slope toward the light). */
const SLOPE_BIAS_TEXELS = 3;
/** Fraction of a splat's size its footprint adds to the filter radius. */
const SPLAT_FOOTPRINT_FILTER = 1.5;
/** Splat bounds for the fit: the 1-99% quantile box of the centres, grown by this fraction. */
const SPLAT_BOUNDS_GROWTH = 0.15;
/**
 * Splats larger than this fraction of the splat bounds' longest side don't cast: a scan's sky and
 * background splats are metres across and would put everything in shadow.
 */
const SPLAT_CASTER_MAX_SIZE_FRACTION = 0.05;
/** Splats fainter than this don't cast (floaters would blotch the ground). */
const SPLAT_CASTER_MIN_OPACITY = 0.6;
/** A streamed splat set re-renders the maps at most this often. */
const SPLAT_VERSION_RERENDER_MS = 400;
/** Splats moving in place (wind) re-render the maps at most this often (20 Hz). */
const SPLAT_MOTION_RERENDER_MS = 50;

/** Follow a changing counter, stepping at most every `interval` ms; returns the value to key on. */
function throttledVersion(state: { value: number; at: number }, next: number, interval: number, now: number): number {
    if (next !== state.value && now - state.at >= interval) {
        state.value = next;
        state.at = now;
    }
    return state.value;
}

const _shadowMatrix = new pc.Mat4();
const _toLight = new pc.Vec3();
const _textureKeys = new WeakMap<object, number>();
let _nextTextureKey = 1;
/** A stable number per texture object, for change keys. */
function textureKey(texture: object): number {
    let key = _textureKeys.get(texture);
    if (key === undefined) _textureKeys.set(texture, key = _nextTextureKey++);
    return key;
}

export class ShadowSystem {
    private readonly host: ShadowSystemHost;
    private readonly directional = new Map<pc.Light, DirectionalState>();
    private readonly local = new Map<pc.Light, LocalState>();
    private readonly managedFits = new Map<pc.Light, DirectionalState>();
    private readonly patchedSplatCasters = new WeakSet<pc.MeshInstance>();
    /** The engine's unified splat draws, collected right before each use (splatMeshInstances.ts). */
    private readonly unifiedSplatDraws = new Set<pc.MeshInstance>();
    private readonly materialKeys = new WeakMap<pc.Material, string>();
    /** Materials this system bound shadow maps to (see bindReceivers). */
    private readonly boundMaterials = new Set<pc.Material>();
    /** Receiver copies no longer bound, freed after the next frame's rebind (afterRender). */
    private readonly retiredCopies: Array<{ texture: pc.Texture; target: pc.RenderTarget }> = [];
    private readonly splatBoundsCache = new WeakMap<object, { version: number; box: pc.BoundingBox | null }>();
    private dirRenderer: DirectionalShadowRendererInternals | null = null;
    private originalCull: DirectionalShadowCull | null = null;
    private localRenderer: LocalShadowRendererInternals | null = null;
    private originalLocalCull: LocalShadowCull | null = null;
    /** The models that cast this frame (from update), for the spot / point culls. */
    private meshCasterInstances: pc.MeshInstance[] = [];
    private readonly localCullCasters: pc.MeshInstance[] = [];
    private groundPlane: ShadowGroundPlane | null = null;
    private settings: ResolvedSceneShadows | null = null;
    private tier: ShadowTier | null = null;
    private splatsCasting = false;
    private slots: Array<{ light: pc.Light; entity: pc.Entity; softness: number; resolution: number; bias: number; authoredIntensity?: number } | null> = [null, null];
    /** Spot / point lights the splats take shadows from (shader slot order), and their entities. */
    private localSlotEntities: Array<pc.Entity | null> = [];
    private readonly localReceivers: LocalShadowReceivers;
    /** Spot / point lights the splats cast into this frame. */
    private readonly splatLocalCasters = new Set<pc.Light>();
    private readonly omniCasterMaterials = new WeakSet<pc.Material>();
    /** WebGPU splat shadow renderers patched to draw into spot / point maps (gsplatLocalCasters.ts). */
    private patchedSplatRenderers = 0;
    private extraFrames = 0;
    private shadowMapRenders = 0;
    private destroyed = false;
    private readonly params = new Float32Array(4 * MAX_SPLAT_SHADOW_LIGHTS);
    private readonly casterLimits = new Float32Array(4);
    private readonly lightData = new Float32Array(4 * MAX_SPLAT_SHADOW_LIGHTS);
    private readonly matrices = new Float32Array(16 * MAX_SPLAT_SHADOW_LIGHTS);
    private readonly global = new Float32Array(4);
    private readonly texel = new Float32Array(4);
    private readonly onPostRender = () => this.afterRender();

    constructor(host: ShadowSystemHost) {
        this.host = host;
        this.localReceivers = new LocalShadowReceivers(host.app);
        const renderer = (host.app as unknown as {
            renderer?: { _shadowRendererDirectional?: DirectionalShadowRendererInternals; _shadowRendererLocal?: LocalShadowRendererInternals };
        }).renderer;
        const dirRenderer = renderer?._shadowRendererDirectional ?? null;
        if (dirRenderer && typeof dirRenderer.cull === 'function') {
            this.dirRenderer = dirRenderer;
            this.originalCull = dirRenderer.cull;
            const original = dirRenderer.cull;
            const managed = this.managedFits;
            const worldLayer = host.worldLayer;
            const splatDraws = this.unifiedSplatDraws;
            dirRenderer.cull = function (this: DirectionalShadowRendererInternals, ...args: Parameters<DirectionalShadowCull>) {
                const [light, comp, camera] = args;
                const state = managed.get(light);
                if (!state?.fit) return original.apply(this, args);
                const splats = state.splatsCast ? { layerCasters: worldLayer.shadowCasters, draws: collectUnifiedSplatDraws(host.app, splatDraws) } : null;
                shadowMapCasters(state.meshCasters, splats, state.cullCasters);
                applyFit(this, light as unknown as pc.Light & LightInternals, comp, camera, state.fit, state.cullCasters);
            };
        } else {
            console.warn('[Shadows] Engine directional shadow renderer not found; shadows follow the camera');
        }
        // Spot / point maps: the same casters as the directional ones (the engine's own cull takes
        // every caster in the light's layers, the editor's helpers included).
        const localRenderer = renderer?._shadowRendererLocal ?? null;
        if (localRenderer && typeof localRenderer.cull === 'function') {
            this.localRenderer = localRenderer;
            this.originalLocalCull = localRenderer.cull;
            const originalLocal = localRenderer.cull;
            const local = this.local;
            const worldLayer = host.worldLayer;
            const splatDraws = this.unifiedSplatDraws;
            const out = this.localCullCasters;
            const system = this;
            localRenderer.cull = function (this: LocalShadowRendererInternals, ...args: Parameters<LocalShadowCull>) {
                const [light, comp, casters] = args;
                if (casters || !local.has(light)) return originalLocal.apply(this, args);
                const splats = system.splatsCasting ? { layerCasters: worldLayer.shadowCasters, draws: collectUnifiedSplatDraws(host.app, splatDraws) } : null;
                return originalLocal.call(this, light, comp, shadowMapCasters(system.meshCasterInstances, splats, out));
            };
        }
        host.app.on('postrender', this.onPostRender);
    }

    /** The resolved settings and tier of the last update (for the editor and diagnostics). */
    getDebugInfo() {
        const lights = this.host.lights().map(({ entity }) => {
            const light = entity.light;
            const internal = light?.light as (pc.Light & LightInternals) | undefined;
            const dir = internal ? this.directional.get(internal) : undefined;
            return {
                name: entity.name,
                type: light?.type,
                castShadows: !!light?.castShadows,
                resolution: light?.shadowResolution,
                shadowType: light?.shadowType,
                fitHalfSize: dir?.fit?.orthoHeight ?? null,
                rendered: dir?.rendered ?? (internal ? this.localReceivers.has(internal) : false),
                splatSlot: this.slots.findIndex((s) => s?.entity === entity),
                /** Spot / point: the splats' slot for this light's shadow (-1 = none). */
                localSlot: this.localSlotEntities.indexOf(entity),
                /** Spot / point: the splats cast into this light's map. */
                splatsCastInto: !!internal && this.splatLocalCasters.has(internal),
            };
        });
        return {
            tier: this.tier?.name ?? 'off',
            enabled: !!this.settings?.enabled && !!this.tier?.enabled,
            legacy: this.settings?.legacy ?? false,
            strength: this.settings?.strength ?? 0,
            splatsCasting: this.splatsCasting,
            splatsReceive: this.settings?.splatsReceive ?? false,
            spotPointOnScan: this.settings?.spotPointOnScan ?? false,
            localAtlasCopies: this.localReceivers.copies,
            groundPlane: !!this.groundPlane?.entity.enabled,
            shadowMapRenders: this.shadowMapRenders,
            lights,
        };
    }

    /** The splat shadow slot (0 / 1) of a light entity, or -1. Feeds the relighting shader. */
    slotOf(entity: pc.Entity): number {
        for (let i = 0; i < this.slots.length; i++) if (this.slots[i]?.entity === entity) return i;
        return -1;
    }

    /** The splats' shadow slot (0-3) of a spot / point light entity, or -1. Feeds the relighting shader. */
    localSlotOf(entity: pc.Entity): number {
        return this.localSlotEntities.indexOf(entity);
    }

    /**
     * True when the splat shader (the relighting shader) must be on this frame: the splats
     * receive shadows, or they cast (it filters the casters and writes point-light depth).
     */
    wantsSplatReceiving(): boolean {
        if (!this.settings?.enabled || !this.tier?.enabled) return false;
        return (!!this.settings.splatsReceive && (this.slots.some(Boolean) || this.localSlotEntities.length > 0)) || this.splatsCasting;
    }

    /** True while the ground plane is on screen material-wise and needs the splat depth map. */
    needsSplatDepth(): boolean {
        return !!this.groundPlane && this.groundPlane.entity.enabled;
    }

    update(): void {
        if (this.destroyed) return;
        const host = this.host;
        this.copyRenderedMaps();
        const lights = host.lights();
        const settings = resolveSceneShadows(host.sceneConfig(), lights.map((l) => l.config));
        const tier = resolveShadowTier({ ...host.tierInput(), mobileMode: settings.mobile });
        this.settings = settings;
        this.tier = tier;
        const active = settings.enabled && tier.enabled;

        // Which lights cast this frame.
        const casting: Array<{ entry: ShadowLightEntry; resolved: ResolvedSceneShadows['lights'][number] }> = [];
        lights.forEach((entry, i) => {
            const comp = entry.entity.light;
            if (!comp) return;
            const resolved = settings.lights[i];
            const cast = active && !!resolved?.cast && entry.entity.enabled && comp.enabled;
            if (cast) casting.push({ entry, resolved });
            else if (comp.castShadows) comp.castShadows = false;
        });
        const directionalCasting = casting.filter(({ entry }) => entry.entity.light?.type === 'directional');
        const localCasting = casting.filter(({ entry }) => entry.entity.light?.type === 'spot' || entry.entity.light?.type === 'point');
        // Spot / point lights reach the scan (scenes from version 3 on, see shadowSettings.ts).
        const localOnScan = settings.spotPointOnScan;
        const splatLocalTargets = localOnScan ? localCasting.slice(0, tier.localSplatCasters) : [];

        // Splats cast when asked for and the tier allows it (desktop): into every directional
        // map, and into the first tier.localSplatCasters spot / point maps.
        const splatsCast = active && settings.splatsCast && tier.splatCasting && (directionalCasting.length > 0 || splatLocalTargets.length > 0);
        if (splatsCast !== this.splatsCasting) {
            this.splatsCasting = splatsCast;
            host.requestFrame('shadows-splat-casting');
        }
        for (const entity of host.splatEntities()) {
            const gs = entity.gsplat;
            if (gs && gs.castShadows !== splatsCast) gs.castShadows = splatsCast;
        }
        this.splatLocalCasters.clear();
        if (splatsCast) {
            for (const { entry } of splatLocalTargets) this.splatLocalCasters.add(entry.entity.light!.light as pc.Light);
            collectUnifiedSplatDraws(host.app, this.unifiedSplatDraws);
            this.patchSplatCasterVisibility();
            if (this.splatLocalCasters.size > 0) {
                this.patchedSplatRenderers = patchGsplatShadowRenderers(host.app, () => this.splatLocalCasters);
                installOmniCasterChunk(host.app.graphicsDevice, host.receiverMaterials(), this.omniCasterMaterials);
            }
        }
        // The shared spot / point map: its size, filter and point-light borders.
        configureLocalAtlas(host.app, localCasting.map(({ entry, resolved }) => ({
            type: entry.entity.light!.type as 'spot' | 'point',
            resolution: resolved.resolution,
            softness: resolved.softness,
        })), tier);

        // Models.
        const anyCast = casting.length > 0;
        const meshCasterInstances: pc.MeshInstance[] = [];
        for (const caster of host.meshCasters()) {
            const cast = anyCast && caster.castShadows !== false;
            const receive = caster.receiveShadows !== false;
            const renders = caster.root.findComponents('render') as pc.RenderComponent[];
            for (const render of renders) {
                if (render.castShadows !== cast) render.castShadows = cast;
                if (render.receiveShadows !== receive) render.receiveShadows = receive;
                if (cast && render.enabled && render.entity.enabled) {
                    for (const mi of render.meshInstances) if (mi.castShadow) meshCasterInstances.push(mi);
                }
            }
        }

        this.meshCasterInstances = meshCasterInstances;

        // Lights.
        const strength = settings.strength;
        const splatCasterInstances = splatsCast ? this.host.worldLayer.shadowCasters.filter((mi) => isSplatMeshInstance(mi, this.unifiedSplatDraws)) : [];
        const splatBoxes = splatsCast ? this.splatBounds() : [];
        this.casterLimits[0] = splatsCast ? splatCasterMaxSize(splatBoxes) : 0;
        this.casterLimits[1] = SPLAT_CASTER_MIN_OPACITY;
        const seenDirectional = new Set<pc.Light>();
        const seenLocal = new Set<pc.Light>();
        for (const { entry, resolved } of casting) {
            const comp = entry.entity.light!;
            const light = comp.light as pc.Light & LightInternals;
            const resolution = Math.min(resolved.resolution, tier.maxResolution);
            const shadowType = lightShadowType(comp.type, resolved.softness);
            if (!comp.castShadows) comp.castShadows = true;
            if (comp.shadowResolution !== resolution) comp.shadowResolution = resolution;
            if (comp.shadowType !== shadowType) comp.shadowType = shadowType;
            if (comp.shadowBias !== resolved.bias) comp.shadowBias = resolved.bias;
            if (comp.normalOffsetBias !== resolved.normalBias) comp.normalOffsetBias = resolved.normalBias;
            if (comp.shadowIntensity !== strength) comp.shadowIntensity = strength;
            if (comp.type === 'directional') {
                seenDirectional.add(light);
                if (comp.numCascades !== 1) comp.numCascades = 1;
                // WebGL2 binds the live map: make sure it exists (see copyForReceivers).
                if (!host.app.graphicsDevice.isWebGPU && !light._shadowMap) this.dirRenderer?.prepareShadowMap?.(light);
                this.updateDirectional(entry.entity, light, resolution, resolved, meshCasterInstances, splatCasterInstances, splatBoxes, shadowType);
            } else {
                seenLocal.add(light);
                this.updateLocal(entry.entity, light, resolution, shadowType, resolved.bias, meshCasterInstances, this.splatLocalCasters.has(light) ? splatCasterInstances.length : 0);
            }
        }
        for (const light of [...this.directional.keys()]) if (!seenDirectional.has(light)) {
            const copy = this.directional.get(light)?.copy;
            if (copy) this.retiredCopies.push(copy);
            this.directional.delete(light);
            this.managedFits.delete(light);
        }
        for (const light of [...this.local.keys()]) if (!seenLocal.has(light)) this.local.delete(light);

        // Splat receiving: the first directional casters, with a rendered map.
        const receive = active && settings.splatsReceive;
        const nextSlots: typeof this.slots = [null, null];
        if (receive) {
            let slot = 0;
            for (const { entry, resolved } of directionalCasting) {
                if (slot >= MAX_SPLAT_SHADOW_LIGHTS) break;
                const light = entry.entity.light!.light as pc.Light;
                nextSlots[slot++] = {
                    light,
                    entity: entry.entity,
                    softness: resolved.softness,
                    resolution: Math.min(resolved.resolution, tier.maxResolution),
                    bias: resolved.bias,
                    authoredIntensity: entry.config.intensity,
                };
            }
        }
        this.slots = nextSlots;
        // ... and the first tier.localSlots spot / point casters (through the atlas copy).
        const localSlots: LocalReceiverSlot[] = [];
        this.localSlotEntities = [];
        if (receive && localOnScan) {
            for (const { entry, resolved } of localCasting) {
                if (localSlots.length >= Math.min(tier.localSlots, MAX_SPLAT_LOCAL_SHADOW_LIGHTS)) break;
                const light = entry.entity.light!.light as pc.Light;
                localSlots.push({
                    light, softness: resolved.softness, bias: resolved.bias, splatsCast: this.splatLocalCasters.has(light),
                    intensity: entry.entity.light!.intensity, authoredIntensity: entry.config.intensity,
                });
                this.localSlotEntities.push(entry.entity);
            }
        }
        this.localReceivers.setLights(localSlots, localCasting.map(({ entry }) => entry.entity.light!.light as pc.Light));
        // A light the splats sample whose map isn't in their copy yet (receiving just turned on
        // for a static light): draw it once more, so the copy gets it.
        for (const light of this.localReceivers.missing()) {
            if (light.shadowUpdateMode === pc.SHADOWUPDATE_NONE) {
                light.shadowUpdateMode = pc.SHADOWUPDATE_THISFRAME;
                host.requestFrame('shadows');
            }
        }
        this.bindReceivers(strength, tier);
        // copies no longer bound (replaced or their light gone); the GPU finishes with them first
        for (const old of this.retiredCopies.splice(0)) {
            old.target.destroy();
            old.texture.destroy();
        }
        this.localReceivers.freeRetired();

        // Ground plane.
        this.updateGroundPlane(active && settings.groundPlane.enabled && anyCast, settings);

        if (this.extraFrames > 0) {
            this.extraFrames--;
            host.requestFrame('shadows-settle');
        }
    }

    /**
     * The map and matrix the splats read must describe the same render, or the shadow jumps
     * for a frame each time a light turns (the flicker creators saw while adjusting a light):
     * - WebGL2 colours the splats in the vertex shader, after this frame's shadow pass: it reads
     *   the live map with the fit being rendered this frame (bindReceivers). A map the engine
     *   is about to create (a new light, a resolution or softness change) is created up front
     *   in update, so there is one to bind.
     * - WebGPU colours them in a projector compute pass that is not ordered against the shadow
     *   pass in a way the viewer can rely on, so it reads a copy of the map, made at the start
     *   of the next update (a copy encoded right after the render could run on the GPU before
     *   the shadow pass had written the map: a one-frame-old map under the new matrix, which
     *   flickered hard while a light turned) and bound together with the fit it was rendered
     *   with. Map and matrix always change together, and the splats never read a map being
     *   redrawn. They follow the models' shadows one frame later.
     */
    private copyForReceivers(light: pc.Light & LightInternals, state: DirectionalState, fit: ShadowFit | null): boolean {
        const map = light._shadowMap;
        const source = map?.renderTargets?.[0];
        if (!map || !source || !fit) return false;
        const device = this.host.app.graphicsDevice;
        const src = map.texture;
        let copy = state.copy;
        if (!copy || copy.texture.width !== src.width || copy.texture.height !== src.height || copy.texture.format !== src.format) {
            const texture = new pc.Texture(device, {
                name: 'SplatShadowMapCopy',
                format: src.format,
                width: src.width,
                height: src.height,
                mipmaps: false,
                minFilter: pc.FILTER_LINEAR,
                magFilter: pc.FILTER_LINEAR,
                addressU: pc.ADDRESS_CLAMP_TO_EDGE,
                addressV: pc.ADDRESS_CLAMP_TO_EDGE,
            });
            texture.compareOnRead = true;
            texture.compareFunc = pc.FUNC_LESS;
            // WebGL2: allocate it now. Left pending, its first bind for sampling would re-upload
            // (empty) storage over the depth just copied in, and the splats would see no shadow.
            (texture.impl as { uploadImmediate?: (device: pc.GraphicsDevice, texture: pc.Texture) => void }).uploadImmediate?.(device, texture);
            const target = new pc.RenderTarget({ name: 'SplatShadowMapCopy', depthBuffer: texture, origin: pc.RENDERTARGET_ORIGIN_NATIVE });
            if (copy) this.retiredCopies.push(copy);
            copy = { texture, target, fit };
            state.copy = copy;
        }
        const copied = (device as pc.GraphicsDevice & { copyRenderTarget(src: pc.RenderTarget, dst: pc.RenderTarget, color: boolean, depth: boolean): boolean })
            .copyRenderTarget(source, copy.target, false, true);
        if (!copied) return false;
        copy.fit = fit;
        return true;
    }

    private updateDirectional(
        entity: pc.Entity,
        light: pc.Light & LightInternals,
        resolution: number,
        resolved: ResolvedSceneShadows['lights'][number],
        meshCasters: pc.MeshInstance[],
        splatCasters: pc.MeshInstance[],
        splatBoxes: pc.BoundingBox[],
        shadowType: number,
    ): void {
        let state = this.directional.get(light);
        if (!state) {
            state = {
                key: '', fit: null, meshCasters: [], splatsCast: false, cullCasters: [], pendingRender: false, renderedFit: null, rendered: false,
                splatVersion: 0, splatVersionAt: -Infinity, splatMotion: 0, splatMotionAt: -Infinity, copy: null, copyFit: null,
            };
            this.directional.set(light, state);
        }
        const boxes: pc.BoundingBox[] = [...splatBoxes];
        for (const mi of meshCasters) boxes.push(mi.aabb);
        // models moving, playing a clip or deforming (shadowChange.ts); the light's own turn is in the fit
        const casterHash = meshCasterHash(meshCasters);
        const fit = fitDirectionalShadow(entity.getRotation(), boxes, resolution);
        state.fit = fit;
        state.meshCasters = meshCasters;
        state.splatsCast = this.splatsCasting;
        if (fit) this.managedFits.set(light, state);
        else this.managedFits.delete(light);

        // Splats streaming in or out (LOD) change the map's content without moving anything;
        // follow them, at most every SPLAT_VERSION_RERENDER_MS.
        if (splatCasters.length > 0 && this.host.splatVersion) {
            const version = this.host.splatVersion();
            const now = performance.now();
            if (version !== state.splatVersion && now - state.splatVersionAt >= SPLAT_VERSION_RERENDER_MS) {
                state.splatVersion = version;
                state.splatVersionAt = now;
            }
        }
        // Splats swaying in the wind move the map's content every frame; follow at most 20 Hz.
        let motion = -1;
        if (splatCasters.length > 0 && this.host.splatMotion) {
            const m = { value: state.splatMotion, at: state.splatMotionAt };
            motion = throttledVersion(m, this.host.splatMotion(), SPLAT_MOTION_RERENDER_MS, performance.now());
            state.splatMotion = m.value;
            state.splatMotionAt = m.at;
        }
        const key = [
            shadowFitKey(fit), resolution, shadowType, resolved.bias, resolved.normalBias,
            splatCasters.length > 0 ? state.splatVersion : -1, splatCasters.length, meshCasters.length, casterHash, motion,
        ].join('|');
        const comp = entity.light!;
        if (key !== state.key) {
            state.key = key;
            if (fit) {
                comp.shadowUpdateMode = pc.SHADOWUPDATE_THISFRAME;
                state.pendingRender = true;
                this.extraFrames = Math.max(this.extraFrames, 2);
                this.host.requestFrame('shadows');
            }
        } else if (!state.pendingRender && comp.shadowUpdateMode !== pc.SHADOWUPDATE_NONE && comp.shadowUpdateMode !== pc.SHADOWUPDATE_THISFRAME) {
            comp.shadowUpdateMode = pc.SHADOWUPDATE_NONE;
        }
    }

    /**
     * A spot / point light: redraw its map (SHADOWUPDATE_THISFRAME) when the light, its settings or
     * its casters changed. `splatCasters`: the splat casters that draw into it (0 = none); their
     * number changes when the splat shadow renderers appear, which must redraw the map too.
     */
    private updateLocal(entity: pc.Entity, light: pc.Light, resolution: number, shadowType: number, bias: number, meshCasters: pc.MeshInstance[], splatCasters: number): void {
        const splatsCast = splatCasters > 0;
        let state = this.local.get(light);
        if (!state) {
            state = { key: '', splatVersion: 0, splatVersionAt: -Infinity, splatMotion: 0, splatMotionAt: -Infinity };
            this.local.set(light, state);
        }
        const comp = entity.light!;
        const m = entity.getWorldTransform().data;
        let h = HASH_SEED;
        for (let i = 0; i < 16; i++) h = hashNumber(h, m[i]);
        h = hashNumber(h, comp.range);
        h = hashNumber(h, comp.outerConeAngle);
        // models moving, playing a clip or deforming (shadowChange.ts)
        h = meshCasterHash(meshCasters, h);
        // A point light's faces are drawn wider for the filter border (configureLocalAtlas).
        if (comp.type === 'point') h = hashNumber(h, localAtlasEdgePixels(this.host.app));
        if (splatsCast && this.host.splatVersion) {
            const version = this.host.splatVersion();
            const now = performance.now();
            if (version !== state.splatVersion && now - state.splatVersionAt >= SPLAT_VERSION_RERENDER_MS) {
                state.splatVersion = version;
                state.splatVersionAt = now;
            }
        }
        if (splatsCast && this.host.splatMotion) {
            const m = { value: state.splatMotion, at: state.splatMotionAt };
            throttledVersion(m, this.host.splatMotion(), SPLAT_MOTION_RERENDER_MS, performance.now());
            state.splatMotion = m.value;
            state.splatMotionAt = m.at;
        }
        const key = `${h}|${resolution}|${shadowType}|${bias}|${meshCasters.length}|${splatsCast ? `s${state.splatVersion}|m${state.splatMotion}|${this.casterLimits[0]}|${splatCasters}|${this.patchedSplatRenderers}` : '-'}`;
        if (key !== state.key) {
            state.key = key;
            comp.shadowUpdateMode = pc.SHADOWUPDATE_THISFRAME;
            this.host.requestFrame('shadows');
        } else if (comp.shadowUpdateMode !== pc.SHADOWUPDATE_NONE && comp.shadowUpdateMode !== pc.SHADOWUPDATE_THISFRAME) {
            comp.shadowUpdateMode = pc.SHADOWUPDATE_NONE;
        }
    }

    /** Robust world bounds of the casting splats (1-99% quantile of the centres, grown a little). */
    private splatBounds(): pc.BoundingBox[] {
        const boxes: pc.BoundingBox[] = [];
        for (const entity of this.host.splatEntities()) {
            const gs = entity.gsplat as (pc.GSplatComponent & { resource?: { centers?: ArrayLike<number> | null; centersVersion?: number; aabb?: pc.BoundingBox | null } | null; customAabb?: pc.BoundingBox | null }) | undefined;
            // Procedural splats (weather follows the camera) cast where the fit happens to
            // cover them, but never move the fit.
            if (!gs || !entity.enabled || !gs.enabled || gs.asset == null) continue;
            const res = gs.resource;
            let local: pc.BoundingBox | null = null;
            if (res?.centers && res.centers.length >= 3) {
                const version = res.centersVersion ?? 0;
                let cached = this.splatBoundsCache.get(res);
                if (!cached || cached.version !== version) {
                    const box = quantileBox(res.centers, 0.01);
                    if (box) box.halfExtents.mulScalar(1 + SPLAT_BOUNDS_GROWTH);
                    cached = { version, box };
                    this.splatBoundsCache.set(res, cached);
                }
                local = cached.box;
            }
            local = local ?? res?.aabb ?? gs.customAabb ?? null;
            if (!local) continue;
            const world = new pc.BoundingBox();
            world.setFromTransformedAabb(local, entity.getWorldTransform());
            boxes.push(world);
        }
        return boxes;
    }

    /**
     * The engine's splat caster (the quad renderer's mesh instance: WebGL2, or the CPU-sorted
     * renderer) accepts every shadow camera. Spot / point maps get the splats only for the lights
     * in splatLocalCasters (a point light draws them six times), so wrap its visibility test
     * (splatCasterVisibility). WebGPU's shadow renderer entries are splat casters too and get the
     * same wrapper around their own test (gsplatLocalCasters.ts).
     */
    private patchSplatCasterVisibility(): void {
        for (const mi of this.host.worldLayer.shadowCasters) {
            if (this.patchedSplatCasters.has(mi) || !isSplatMeshInstance(mi, this.unifiedSplatDraws)) continue;
            const original = mi.isVisibleFunc;
            if (!original) continue;
            mi.isVisibleFunc = splatCasterVisibility(original as (camera: pc.Camera) => boolean, this.splatLocalCasters);
            this.patchedSplatCasters.add(mi);
        }
    }

    private bindReceivers(strength: number, tier: ShadowTier): void {
        const host = this.host;
        const device = host.app.graphicsDevice;
        const camera = host.camera()?.camera ?? null;
        // With relighting on, a shadow removes the shadowed light (the viewer's lights button
        // fades the light out, and its shadows with it). With it off, a shadow darkens the baked
        // colour by the strength. The shader picks the mode from uSplatShadowGlobal.y.
        const relighting = host.relighting();
        const useCopy = device.isWebGPU;
        const textures: pc.Texture[] = [];
        let key = '';
        for (let slot = 0; slot < MAX_SPLAT_SHADOW_LIGHTS; slot++) {
            const s = this.slots[slot];
            const light = s?.light as (pc.Light & LightInternals) | undefined;
            const state = light ? this.directional.get(light) : undefined;
            // WebGPU: the copy of the last rendered map with its fit; WebGL2: the live map with
            // the fit it holds after this frame's shadow pass (see copyForReceivers).
            const texture = useCopy ? state?.copy?.texture : light?._shadowMap?.texture;
            const fit = useCopy ? state?.copy?.fit ?? null : state?.pendingRender && state.fit ? state.fit : state?.rendered ? state.renderedFit : null;
            const o4 = slot * 4;
            if (!s || !light || !state || !texture || !fit || !camera) {
                this.params.fill(0, o4, o4 + 4);
                this.lightData.fill(0, o4, o4 + 4);
                this.matrices.fill(0, slot * 16, slot * 16 + 16);
                this.texel[slot] = 0;
                textures.push(getShadowMapPlaceholder(device));
                key += `|-`;
                continue;
            }
            const res = s.resolution;
            const worldToUv = 1 / (2 * fit.orthoHeight);
            // relighting off: the shadow fades with the light's intensity (animated or set below
            // what was authored); relighting on already scales the shadowed light by it
            this.params[o4] = relighting.enabled ? strength : shadowStrengthForLight(strength, s.entity.light?.intensity ?? 1, s.authoredIntensity);
            this.params[o4 + 1] = softnessToKernelTexels(s.softness) / res;
            this.params[o4 + 2] = s.bias;
            this.params[o4 + 3] = this.splatsCasting ? SPLAT_SIZE_BIAS_CASTING : SPLAT_SIZE_BIAS_MESHES_ONLY;
            // Toward the light as the map was rendered (the shadow camera's +Z), not the light's
            // current direction: on the frame a light turns, the bias must follow the map in use.
            fit.rotation.transformVector(pc.Vec3.BACK, _toLight);
            this.lightData[o4] = _toLight.x;
            this.lightData[o4 + 1] = _toLight.y;
            this.lightData[o4 + 2] = _toLight.z;
            this.lightData[o4 + 3] = worldToUv * SPLAT_FOOTPRINT_FILTER;
            this.matrices.set(fitShadowMatrix(fit, _shadowMatrix).data, slot * 16);
            this.texel[slot] = (2 * fit.orthoHeight) / res;
            textures.push(texture);
            key += `|${textureKey(texture)}`;
        }
        this.global[0] = tier.splatTaps;
        this.global[1] = relighting.enabled ? 1 : 0;
        this.texel[2] = SLOPE_BIAS_TEXELS;
        const local = this.localReceivers.fill(strength, relighting.enabled);
        key += `|${Array.from(this.params).join(',')}|${Array.from(this.lightData).join(',')}|${Array.from(this.matrices).join(',')}|${this.global[0]},${this.global[1]}|${Array.from(this.texel).join(',')}|${this.casterLimits[0]},${this.casterLimits[1]}`;
        key += `|${local.key}|${textureKey(local.texture)}`;
        const materials = host.receiverMaterials();
        // A material that left the list (its relighting script was switched off while another
        // effect keeps the shader) still samples the maps it was given: when a light stops
        // casting its map is destroyed, and binding a destroyed texture fails on WebGPU. Give
        // every such material the placeholders.
        for (const material of this.boundMaterials) {
            if (materials.includes(material)) continue;
            this.boundMaterials.delete(material);
            this.materialKeys.delete(material);
            const placeholder = getShadowMapPlaceholder(device);
            SPLAT_SHADOW_UNIFORMS.maps.forEach((name) => material.setParameter(name, placeholder));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.params, new Float32Array(4 * MAX_SPLAT_SHADOW_LIGHTS));
            LocalShadowReceivers.clear(material, device);
            material.update();
            host.requestFrame('shadows-receivers');
        }
        for (const material of materials) {
            if (this.materialKeys.get(material) === key) continue;
            this.materialKeys.set(material, key);
            this.boundMaterials.add(material);
            material.setParameter(SPLAT_SHADOW_UNIFORMS.params, new Float32Array(this.params));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.light, new Float32Array(this.lightData));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.matrix, new Float32Array(this.matrices));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.global, new Float32Array(this.global));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.texel, new Float32Array(this.texel));
            material.setParameter(SPLAT_SHADOW_UNIFORMS.casterLimits, new Float32Array(this.casterLimits));
            SPLAT_SHADOW_UNIFORMS.maps.forEach((name, i) => material.setParameter(name, textures[i]));
            this.localReceivers.apply(material, local.texture);
            // Unified renderers only re-copy the template material's parameters when it is dirty.
            material.update();
            host.requestFrame('shadows-receivers');
        }
    }

    private updateGroundPlane(wanted: boolean, settings: ResolvedSceneShadows): void {
        if (!wanted) {
            if (this.groundPlane?.entity.enabled) {
                this.groundPlane.entity.enabled = false;
                this.host.requestFrame('shadows-ground-plane');
            }
            return;
        }
        if (!this.groundPlane) {
            this.groundPlane = createShadowGroundPlane(this.host.app, this.host.afterSplatsLayer);
            this.host.sceneRoot.addChild(this.groundPlane.entity);
        }
        if (this.groundPlane.set(settings.groundPlane.y, settings.groundPlane.scale) || !this.groundPlane.entity.enabled) {
            this.groundPlane.entity.enabled = true;
            this.host.requestFrame('shadows-ground-plane');
        }
    }

    private afterRender(): void {
        for (const state of this.directional.values()) {
            if (!state.pendingRender) continue;
            state.pendingRender = false;
            state.rendered = true;
            state.renderedFit = state.fit;
            this.shadowMapRenders++;
            // WebGPU copies the map at the start of the next update (copyRenderedMaps): copied
            // here, the copy can run on the GPU before this frame's shadow pass has written it.
            if (this.host.app.graphicsDevice.isWebGPU) state.copyFit = state.fit;
        }
        // Spot / point maps: where each light's map sits in the atlas just rendered, and the
        // splats' copy of it (WebGL2 now, WebGPU at the start of the next update; localShadows.ts).
        this.shadowMapRenders += this.localReceivers.afterRender();
    }

    /** WebGPU: copy the maps rendered last frame for the splats (see copyForReceivers). */
    private copyRenderedMaps(): void {
        for (const [light, state] of this.directional) {
            if (!state.copyFit) continue;
            this.copyForReceivers(light as pc.Light & LightInternals, state, state.copyFit);
            state.copyFit = null;
        }
        this.localReceivers.copyPending();
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.host.app.off('postrender', this.onPostRender);
        if (this.dirRenderer && this.originalCull) this.dirRenderer.cull = this.originalCull;
        if (this.localRenderer && this.originalLocalCull) this.localRenderer.cull = this.originalLocalCull;
        this.meshCasterInstances = [];
        this.groundPlane?.destroy();
        this.groundPlane = null;
        // the lights' maps may go next: leave no material pointing at them
        const placeholder = getShadowMapPlaceholder(this.host.app.graphicsDevice);
        for (const material of this.boundMaterials) {
            SPLAT_SHADOW_UNIFORMS.maps.forEach((name) => material.setParameter(name, placeholder));
            LocalShadowReceivers.clear(material, this.host.app.graphicsDevice);
            material.update();
        }
        this.boundMaterials.clear();
        for (const state of this.directional.values()) if (state.copy) this.retiredCopies.push(state.copy);
        for (const old of this.retiredCopies.splice(0)) {
            old.target.destroy();
            old.texture.destroy();
        }
        this.localReceivers.destroy();
        this.splatLocalCasters.clear();
        this.managedFits.clear();
        this.directional.clear();
        this.local.clear();
    }
}

/** The engine's directional cull, with the shadow camera placed by `fit` instead of the view. */
function applyFit(
    self: DirectionalShadowRendererInternals,
    light: pc.Light & LightInternals,
    comp: pc.LayerComposition,
    camera: pc.Camera,
    fit: ShadowFit,
    casters: pc.MeshInstance[],
): void {
    // No distance fade: the fit, not the view distance, decides what is covered.
    light._shadowCascadeDistances.fill(1e9);
    const rd = light.getRenderData(camera, 0);
    const shadowCam = rd.shadowCamera;
    if (light._shadowMap) shadowCam.renderTarget = light._shadowMap.renderTargets[0];
    rd.shadowViewport.copy(light.cascades[0]);
    rd.shadowScissor.copy(light.cascades[0]);
    const node = shadowCam._node;
    node.setPosition(fit.position);
    node.setRotation(fit.rotation);
    shadowCam.nearClip = fit.nearClip;
    shadowCam.farClip = fit.farClip;
    shadowCam.orthoHeight = fit.orthoHeight;
    shadowCam.updateFrustum();
    self.shadowRenderer.cullShadowCasters(comp, light, rd.visibleCasters, shadowCam, casters);
    rd.projectionCompensation = fit.orthoHeight;
}

/**
 * The casters a shadow map (directional, spot or point) is culled from: the models that cast and
 * the splat draws, nothing else from the lights' layers (the editor's helpers draw there).
 *
 * They are resolved when the engine culls the map (during the render), not in update. Between
 * the two, the engine's gsplat director rebuilds splat renderers and destroys their mesh
 * instances: when a camera that draws the World layer leaves the frame (a mirror's or a water
 * plane's reflection camera turning off out of view), when the splat renderer is switched, or
 * when the set of casting splats changes. A destroyed instance keeps castShadow, and the WebGL2
 * quad renderer's visibility test still accepts shadow cameras, so the engine's caster cull keeps
 * it; its caster sort then compares it with the live splat casters (they share a shadow shader)
 * by mesh id, and its mesh is null ("Cannot read properties of null (reading 'id')" in
 * sortCompareShader). So the splats come from the layer's casters as they are now (the engine
 * removes an instance there before destroying it), picked out with the splat draws collected now
 * (`splats.draws`), and the models from update, minus any destroyed since. `splats` is null when
 * the splats don't cast. Fills and returns `out`.
 */
export function shadowMapCasters(
    models: ReadonlyArray<pc.MeshInstance>,
    splats: { layerCasters: ReadonlyArray<pc.MeshInstance>; draws: ReadonlySet<pc.MeshInstance> } | null,
    out: pc.MeshInstance[],
): pc.MeshInstance[] {
    out.length = 0;
    for (const mi of models) if (mi.mesh && mi.material) out.push(mi);
    if (splats) for (const mi of splats.layerCasters) if (mi.mesh && isSplatMeshInstance(mi, splats.draws)) out.push(mi);
    return out;
}

/**
 * A splat caster's visibility test limited to the spot / point lights the splats cast into:
 * orthographic (directional) shadow cameras and non-shadow cameras go to `original`, perspective
 * shadow cameras only when they are a face camera of a light in `casters` as it has them at cull
 * time. Face cameras collected earlier in the frame went stale whenever the update then changed
 * the light (its shadow type, map size, Cast Shadows): the engine dropped them and drew the map
 * with new ones, which this test turned away, so the map was drawn without the splats and kept
 * that way until the light changed again.
 */
export function splatCasterVisibility(original: (camera: pc.Camera) => boolean, casters: ReadonlySet<pc.Light>): (camera: pc.Camera) => boolean {
    return (camera: pc.Camera) => {
        if (camera.node?.name === SHADOW_CAMERA_NAME && camera.projection !== pc.PROJECTION_ORTHOGRAPHIC) {
            let ours = false;
            for (const light of casters) if (isLightFaceCamera(light, camera)) { ours = true; break; }
            if (!ours) return false;
        }
        return original(camera);
    };
}

/**
 * The engine shadow type for a light: PCF5 once it is soft, PCF3 below, and always PCF3 for a
 * point light. The engine filters point (omni) maps with PCF1 / PCF3 / PCSS only and turns a PCF5
 * request into PCF3, so asking for PCF5 never matched what the light reported back: update set it
 * again every frame, and each set made the engine drop the light's render data (its face cameras)
 * and redraw all six faces, every frame, so the view never idled; with the face cameras the update
 * had seen gone, the scan was also left out of every one of those maps (splatCasterVisibility).
 * A point light's softness still shows: on models through the shared atlas filter
 * (configureLocalAtlas), on the scan through the splat shader's own filter.
 */
export function lightShadowType(type: string | undefined, softness: number): number {
    return type !== 'point' && softness >= 0.5 ? pc.SHADOW_PCF5_32F : pc.SHADOW_PCF3_32F;
}

/** Largest splat that casts: a fraction of the longest side of the splat bounds (0 = no limit). */
export function splatCasterMaxSize(boxes: ReadonlyArray<pc.BoundingBox>): number {
    let longest = 0;
    for (const b of boxes) longest = Math.max(longest, 2 * b.halfExtents.x, 2 * b.halfExtents.y, 2 * b.halfExtents.z);
    return longest > 0 ? longest * SPLAT_CASTER_MAX_SIZE_FRACTION : 0;
}
